import React, { useState } from 'react';
import axios from 'axios';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import useMediaQuery from '@mui/material/useMediaQuery';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import { useTheme } from '@mui/material/styles';

import { ActionButtons } from '../../others/ActionButtons';
import LoadingOverlay from '../../others/LoadingOverlay';
import DynamicForm from '../../dynamicForm/DynamicForm';

import { useValidateDynamicForm } from '../../../hooks/useValidateDynamicForm';
import { useNotification } from '../../../hooks/useNotification';
import { usePWAInstall } from '../../../hooks/usePWAInstall';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useApiResolver } from '../../../hooks/useApiResolver';

import { ensureArray } from '../../../utils/validateResponseApi';
import { isNotEmptyObject } from '../../../utils/jsonValidator';
import { ActionIconItem } from '../../others/ActionIconItem';

const baseUrl = process.env.REACT_APP_AWS_API_GATEWAY_URL_EXECUTE_FLOW;

export const ModalForms = ({ data, dtoReg, handleChange, variables = {} }) => {
    const { t } = useTranslation();
    const { resolveValue } = useApiResolver();
    const { isMobileScreen } = usePWAInstall();
    const [{ userSession }] = useDataAuth();
    const [errorInputs, validForm, resetErrors] = useValidateDynamicForm();
    const [sendNotification] = useNotification();
    const [open, setOpen] = useState(false);
    const [isLoading, setisLoading] = useState(false);
    const [fileBytes, setFileBytes] = useState(null);
    const [file, setFile] = useState(null);

    const theme = useTheme();
    useMediaQuery(theme.breakpoints.down('sm'));  // 📱 Fullscreen en móvil

    const [contentJson, setContentJson] = useState(() =>
        data?.form?.reduce((json, element) => {
            if (dtoReg.hasOwnProperty(element.id)) {
                console.log('dtoReg[element.id]:', element.id, (dtoReg[element.id]).toString());
                json[element.id] = (dtoReg[element.id]).toString();
            }
            return json;
        }, { id: dtoReg.id }) || { id: dtoReg.id }
    );

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setContentJson({ ...contentJson, [name]: type === 'checkbox' ? checked : value });
    };

    const setChanges = (name, value) => {
        setContentJson(prev => ({
            ...prev,
            [name]: value,
        }));
    }

    const setValuesSelectGeneric = (e) => {
        const { name, value, fullObject } = e.target;
        setChanges(name, value);
        if (isNotEmptyObject(fullObject))
            ensureArray(data?.form).forEach((element) => {
                if (fullObject.hasOwnProperty(element.id)) {
                    setChanges(`${name}_${element.id}`, fullObject[element.id]);
                    setChanges(element.id, fullObject[element.id]);
                }
            })
    }

    const setFileTemp = (bytes, field) => {
        const u8 = new Uint8Array(bytes);
        const b64 = Buffer.from(u8).toString('base64');
        setChanges(field + '_file_B64', b64);
        setFileBytes(bytes);
    };

    const handleFileChange = (event, field) => {
        const fileSelected = event.target.files[0];
        setFile(fileSelected);
        setChanges(field, fileSelected?.name);

        if (fileSelected) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const bytes = new Uint8Array(e.target.result);
                setFileTemp(bytes, field);
            };
            reader.readAsArrayBuffer(fileSelected);
        }
        event.target.value = '';
    };

    const handleUploadClick = async () => {
        if (!fileBytes) return;

        try {
            const u8 = new Uint8Array(fileBytes);
            const b64 = Buffer.from(u8).toString('base64');

            const bodyData = {
                id_flow: "260",
                id: dtoReg.id,
                name: file.name.replace(/\s+/g, '_'),
                file: b64
            };

            const response = await axios.post(baseUrl, bodyData, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userSession.token}`
                },
            });

            if (response.status === 200) {
                Object.keys(response.data).forEach((key) => {
                    contentJson[key] = response.data[key];
                });
                sendNotification({ msg: t("messages.fileUploadSuccess"), variant: 'info' });
            } else {
                sendNotification({ msg: t("messages.saveError"), variant: 'error' });
            }
        } catch (error) {
            sendNotification({ msg: t("messages.saveError"), variant: 'error' });
        }
    };

    const CallApi = async (param) => {
        if (!validForm(data.form, contentJson)) return;

        setisLoading(true);
        setOpen(false);

        const { url, method, bodyData } = param[0];
        if (variables?.id_company) {
            bodyData.id_company = variables.id_company;
            bodyData.select_company = variables?.select_company || false;
        }
        let str = JSON.stringify(bodyData);

        for (let key in contentJson) {
            let value = (contentJson[key] || "").toString().replace(/"/g, '\\"').replace(/[\r\n]+/g, ' ');
            if (value === `{${key}}`)
                value = ''
            str = str.replace(`{${key}}`, value).replace(`{[form].${key}}`, value);
        }

        const { success } = await resolveValue({
            method,
            url,
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${userSession.token}`,
            },
            data: str,
        });

        if (success)
            setTimeout(() => handleChange(), 500);

        setisLoading(false);
    };

    return (
        <>
            <ActionIconItem
                item={data}
                onClick={() => {
                    setOpen(true);
                    resetErrors();
                }}
                defaultIcon="DynamicForm"
                badgeContent={data?.badge || null}
            />

            <Dialog
                open={open}
                fullScreen={isMobileScreen}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxWidth: data.width ? `${data.width}%` : 600,
                        maxHeight: "90vh",
                        borderRadius: isMobileScreen ? 0 : 2,
                        display: "flex",
                        flexDirection: "column",
                    },
                }}
            >
                {/* ---------- HEADER ---------- */}
                <DialogTitle sx={{ borderBottom: '1px solid #ccc' }}>
                    <Typography variant="h6">{data.accion}</Typography>
                </DialogTitle>

                {/* ---------- BODY ---------- */}
                <DialogContent sx={{ overflowY: 'auto', flexGrow: 1 }}>
                    <DynamicForm
                        values={contentJson}
                        scheme={data.form}
                        errorInputs={errorInputs}
                        onChange={onChange}
                        handleFileChange={handleFileChange}
                        handleUploadClick={handleUploadClick}
                        setChanges={setChanges}
                        setValuesSelectGeneric={setValuesSelectGeneric}
                        variables={variables}
                    />
                </DialogContent>

                {/* ---------- FOOTER ---------- */}
                <DialogActions sx={{ borderTop: '1px solid #ccc' }}>
                    <ActionButtons
                        onSubmit={() => CallApi(data.trigger)}
                        onCancel={() => setOpen(false)}
                    />
                </DialogActions>
            </Dialog>

            <LoadingOverlay isOpen={isLoading} />
        </>
    );
};
